// src/pages/AddGroupPage.tsx
import React, { useEffect, useState } from "react";
import { apiUrl } from "../config/api";
import ServerSearchBar from "../components/ServerSearchBar";


type GroupRow = { id: number; group_name: string };

export default function AddGroupPage(): JSX.Element {
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // [ADD] รายการกลุ่มที่มีอยู่
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listErr, setListErr] = useState("");

  const [q, setQ] = useState<string>("");

  const [toast, setToast] = useState<{ show: boolean; message: string; tone: "success" | "error" }>({
    show: false,
    message: "",
    tone: "success",
  });

  const showToast = (message: string, tone: "success" | "error" = "success") => {
    setToast({ show: true, message, tone });
    setTimeout(() => setToast({ show: false, message: "", tone: "success" }), 1800);
  };

  // [ADD] โหลดรายการกลุ่ม
  const loadGroups = async (query?: string): Promise<void> => {
    setLoadingList(true);
    setListErr("");
    try {
      const qs: string[] = [];
      if (query && query.trim()) qs.push(`q=${encodeURIComponent(query.trim())}`);
  
      const url = apiUrl(`/groups_list${qs.length ? "?" + qs.join("&") : ""}`);
      const resp = await fetch(url);
  
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
  
      const items: GroupRow[] = Array.isArray(j?.items) ? j.items : [];
      setGroups(items);
    } catch (e: any) {
      setListErr(e?.message ?? "โหลดรายการกลุ่มไม่สำเร็จ");
    } finally {
      setLoadingList(false);
    }
  };

  const handleSearch = (query: string | undefined): void => {
    const qVal = (query ?? "").trim();
    setQ(qVal);
    void loadGroups(qVal || undefined);
  };
  
  const handleRefresh = (): void => {
    void loadGroups(q || undefined);
  };

  useEffect(() => {
    void loadGroups(undefined);
  }, []);
  
  

  // PMN-CRITICAL(/AddGroupPage): This inserts groups_type master data used by ProductsAdd/Edit; duplicate/trim behavior must stay aligned with backend.
  const save = async () => {
    if (!groupName.trim()) {
      setErr("กรุณากรอกชื่อกลุ่ม");
      return;
    }

    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(apiUrl("/groupsAdd"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_name: groupName.trim() }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || j?.ok !== true) throw new Error(j?.error ?? resp.statusText);

      showToast("บันทึกสำเร็จ ✅", "success");
      setGroupName("");

      // [ADD] รีเฟรชรายการทางขวาทันที
      await loadGroups(q || undefined);

    } catch (e: any) {
      const msg = e?.message || "บันทึกไม่สำเร็จ";
      setErr(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div
            className={`alert ${toast.tone === "error" ? "alert-danger" : "alert-success"} shadow mb-0 rounded-3 py-2 px-3`}
          >
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-primary text-center">เพิ่มกลุ่มสินค้า (Add Group)</h4>

                <div className="row g-4">
                  {/* ซ้าย: รายการกลุ่มที่ถูกสร้างแล้ว */}
                  <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm rounded-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="fw-semibold">รายการกลุ่มที่มีอยู่</div>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => void loadGroups()}
                            disabled={loadingList}
                          >
                            {loadingList ? "กำลังโหลด…" : "รีเฟรช"}
                          </button>
                        </div>

                        <ServerSearchBar
                          value={q}
                          onSearch={handleSearch}
                          onRefresh={handleRefresh}
                          loading={loadingList}
                          placeholder="ค้นหาชื่อกลุ่ม (กด Enter เพื่อค้นหา server)"
                        />

                        {listErr && <div className="alert alert-warning py-2">{listErr}</div>}

                        <div
                          className="list-group"
                          style={{ maxHeight: 420, overflowY: "auto", overflowX: "auto" }}
                        >
                          {groups.map((g) => (
                            <div
                              key={g.id}
                              className="list-group-item d-flex justify-content-between align-items-center"
                              style={{ minWidth: "max-content" }}   // 👈 บังคับให้รายการยาวได้จริง
                            >
                              <span
                                style={{
                                  whiteSpace: "nowrap",
                                  display: "inline-block"
                                }}
                                title={g.group_name}
                              >
                                {g.group_name}
                              </span>

                              <span className="badge bg-light text-muted">#{g.id}</span>
                            </div>
                          ))}

                          {!loadingList && groups.length === 0 && (
                            <div className="list-group-item text-muted text-center">ยังไม่มีกลุ่ม</div>
                          )}
                        </div>

                        <div className="small text-muted mt-2">
                          ทั้งหมด {groups.length.toLocaleString()} กลุ่ม
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* ขวา: ฟอร์มเพิ่มกลุ่ม */}
                  <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm rounded-3">
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label">ชื่อกลุ่มสินค้า</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="กรอกชื่อกลุ่ม เช่น ของเล่น, ของตกแต่งบ้าน"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                          />
                        </div>

                        {err && <div className="alert alert-warning py-2">{err}</div>}

                        <div className="d-flex justify-content-between">
                          <a href="/" className="btn btn-link text-decoration-none">← กลับหน้าเมนู</a>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={loading || !groupName.trim()}
                            onClick={() => void save()}
                          >
                            {loading ? "กำลังบันทึก..." : "บันทึกกลุ่ม"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  
                </div>

                <div className="text-center small text-muted mt-3">
                  * ใช้ API: <code>POST /groupsAdd</code> และ <code>GET /groups_list</code>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
